import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Link } from 'react-router-dom'
import type { Project } from '@1130-studio/shared'
import { cn, formatDate } from '../../lib/utils'

interface ProjectCardProps {
  project: Project
  isDragging?: boolean
}

const priorityColors = {
  urgent: 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.5)]',
  high: 'bg-accent-orange',
  medium: 'bg-accent-blue',
  low: 'bg-text-tertiary',
}

const priorityAccents = {
  urgent: 'var(--accent-orange)',
  high: 'var(--accent-orange)',
  medium: 'var(--accent-blue)',
  low: 'rgba(255, 255, 255, 0.5)',
}

function formatBudget(budget: string | undefined | null): string | null {
  if (!budget) return null
  const num = parseFloat(budget)
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(0)}K`
  }
  return `${num}`
}

function getDeadlineStatus(deadline: Date | string | undefined | null): 'normal' | 'soon' | 'overdue' {
  if (!deadline) return 'normal'
  const date = new Date(deadline)
  const now = new Date()
  const daysUntil = Math.ceil((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))

  if (daysUntil < 0) return 'overdue'
  if (daysUntil <= 7) return 'soon'
  return 'normal'
}

function getClientInitials(name: string): string {
  const words = name.split(' ')
  if (words.length >= 2) {
    return words[0][0] + words[1][0]
  }
  return name.slice(0, 2).toUpperCase()
}

export function ProjectCard({ project, isDragging }: ProjectCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: project.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    '--card-accent': priorityAccents[project.priority],
  } as React.CSSProperties

  const deadlineStatus = getDeadlineStatus(project.deadline)
  const budget = formatBudget(project.budget)
  const clientName = project.client?.companyName || project.client?.contactName

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={cn(
        'group relative glass-card rounded-[14px] p-4 cursor-grab',
        'transition-all duration-200 overflow-hidden',
        'hover:-translate-y-0.5',
        'before:absolute before:top-0 before:left-0 before:right-0 before:h-[3px]',
        'before:bg-[var(--card-accent)] before:opacity-0 before:transition-opacity',
        'hover:before:opacity-100',
        isDragging && 'opacity-50 rotate-[3deg]'
      )}
    >
      {/* Quick Actions */}
      <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <Link
          to={`/projects/${project.id}`}
          className="w-7 h-7 rounded-md bg-white/10 border border-white/10 flex items-center justify-center text-text-tertiary hover:bg-white hover:text-black transition-all"
          onClick={(e) => e.stopPropagation()}
        >
          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        </Link>
      </div>

      {/* Header */}
      <div className="flex items-start justify-between mb-3 pr-8">
        <span className="text-[15px] font-semibold leading-snug text-white">
          {project.name}
        </span>
        <div
          className={cn(
            'w-2 h-2 rounded-full flex-shrink-0 mt-1.5',
            priorityColors[project.priority]
          )}
        />
      </div>

      {/* Client */}
      {clientName && (
        <div className="flex items-center gap-2 mb-4">
          <div className="w-6 h-6 rounded-md bg-white/10 border border-white/10 flex items-center justify-center text-[11px] font-semibold text-text-secondary">
            {getClientInitials(clientName)}
          </div>
          <span className="text-[13px] text-text-secondary">{clientName}</span>
        </div>
      )}

      {/* Meta */}
      <div className="flex items-center gap-4 pt-3 border-t border-white/10">
        {project.deadline && (
          <div
            className={cn(
              'flex items-center gap-1.5 text-xs',
              deadlineStatus === 'normal' && 'text-text-secondary',
              deadlineStatus === 'soon' && 'text-accent-orange',
              deadlineStatus === 'overdue' && 'text-red-400'
            )}
          >
            <svg className="w-3.5 h-3.5 opacity-60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            {formatDate(project.deadline)}
          </div>
        )}

        {budget && (
          <div className="ml-auto font-mono text-xs font-medium text-white bg-white/10 border border-white/10 px-2 py-1 rounded-md">
            {budget} ₽
          </div>
        )}
      </div>
    </div>
  )
}

export function ProjectCardDragOverlay({ project }: { project: Project }) {
  const budget = formatBudget(project.budget)
  const clientName = project.client?.companyName || project.client?.contactName

  return (
    <div
      style={{ '--card-accent': priorityAccents[project.priority] } as React.CSSProperties}
      className="glass-card rounded-[14px] p-4 shadow-[0_4px_24px_rgba(0,0,0,0.4)] rotate-[3deg] cursor-grabbing"
    >
      <div className="flex items-start justify-between mb-3">
        <span className="text-[15px] font-semibold leading-snug text-white">
          {project.name}
        </span>
        <div className={cn('w-2 h-2 rounded-full flex-shrink-0 mt-1.5', priorityColors[project.priority])} />
      </div>

      {clientName && (
        <div className="flex items-center gap-2 mb-4">
          <div className="w-6 h-6 rounded-md bg-white/10 border border-white/10 flex items-center justify-center text-[11px] font-semibold text-text-secondary">
            {getClientInitials(clientName)}
          </div>
          <span className="text-[13px] text-text-secondary">{clientName}</span>
        </div>
      )}

      <div className="flex items-center gap-4 pt-3 border-t border-white/10">
        {project.deadline && (
          <div className="flex items-center gap-1.5 text-xs text-text-secondary">
            <svg className="w-3.5 h-3.5 opacity-60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            {formatDate(project.deadline)}
          </div>
        )}
        {budget && (
          <div className="ml-auto font-mono text-xs font-medium text-white bg-white/10 border border-white/10 px-2 py-1 rounded-md">
            {budget} ₽
          </div>
        )}
      </div>
    </div>
  )
}
